/**
 * Copyright © Veeam Software Group GmbH.
 */

/**
 * Copyright © Veeam Software Group GmbH.
 */

import React, { createContext, useEffect, useState } from 'react';

import type { CSSProperties, PropsWithChildren, RefObject } from 'react';

import { ValidationState, FormState } from '../../entries';
import { KeyboardKeys } from '../../../../core';

import type { FormErrors, ExternalFormApi } from '../../interfaces';

export const FormsContext = createContext<FormState<any>>(new FormState(null));

interface FormsProps<V> {
    value: V;
    validate?: (values: V) => FormErrors<V>;
    onChange?: (v: V, changedFields: string[], SetFormValue) => void;
    validationState?: ValidationState;
    externalFormApi?: ExternalFormApi<V>;
    style?: CSSProperties;
    onSubmit?: (value: V) => void;
    fields?: Array<{ name: string; ref: RefObject<any>; }>;
}

export const Form = <V,>(props: PropsWithChildren<FormsProps<V>>) => {
    const {
        children,
        style,
        externalFormApi,
        onSubmit,
        onChange,
        fields,
        validationState,
        validate,
        value,
    } = props;
    const [formsState] = useState<FormState<V>>(() => {
        const fs = new FormState<V>(validate);
        fs.value = value;
        fs.fixedStartValue = JSON.stringify(fs.value);
        fs.validate();
        return fs;
    });
    const onKeyDownHandler = (e: React.KeyboardEvent) => {
        if (e.key === KeyboardKeys.Enter) {
            externalFormApi?.submit();
        }
    };

    formsState.setValidator(validate);

    if (validationState) {
        formsState.setValidationState(validationState);
    } else {
        if (!formsState.validationState) {
            formsState.setValidationState(new ValidationState());
        }
    }

    if (externalFormApi) {
        externalFormApi.setValue = (path: string | V, value?: any) => {
            formsState.setValue(path, value);
        };
        externalFormApi.isValid = () => formsState.isValid();
        externalFormApi.validate = () => formsState.validate();
        externalFormApi.isDataChanged = () => formsState.isDataChanged();
        externalFormApi.submit = () => {
            // formsState.validationState.markAsForce();
            // formsState.handleNewValue();
            fields.forEach(({ ref }) => ref.current?.focus());
            fields.forEach(({ ref }) => ref.current?.blur());

            const isValid = externalFormApi.isValid();

            if (isValid) {
                onSubmit(formsState.value);
            } else if (fields && fields.length > 0) {
                fields.every(({ name, ref }) => {
                    if (ref.current && formsState.getError(name)) {
                        ref.current.focus();
                        return false;
                    }

                    return true;
                });
            }
        };
        externalFormApi.value = formsState.value;
        externalFormApi.setExternalErrors = (errors: FormErrors<V>) => formsState.setExternalErrors(errors);
        externalFormApi.getExternalErrors = () => formsState.getExternalErrors();
    }

    useEffect(() => {
        formsState.onChange((value) => {
            onChange?.(value, [], () => {});
        });
        return () => {
            formsState.destroyObject();
        };
    }, []);

    return (
        onSubmit
            ? (
                <form
                    style={style}
                    onKeyDown={onKeyDownHandler}
                    onSubmit={(e) => {
                        e.preventDefault();
                        return false;
                    }}
                    action=''
                >
                    <FormsContext.Provider value={formsState}>
                        {children}
                    </FormsContext.Provider>
                </form>
            )
            : (
                <div
                    style={style}
                >
                    <FormsContext.Provider value={formsState}>
                        {children}
                    </FormsContext.Provider>
                </div>
            )
    );
};
